import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getRuntimeEnv } from './runtime-config';

interface ApiServiceResponse<T> {
    data: T;
    status: number;
}

const CRM_LOGIN_URL = 'https://haat-crm-backend.haat.delivery/auth/login';

//i sla,
function getTokenResturant(): string {
    const token = localStorage.getItem('TOKEN_RESTURANT') || '';
    return token.replace(/^"|"$/g, ''); // This regex replaces quotes at the start and end of the string
}
function profileUrl(): string | null {
    const base = getRuntimeEnv('REACT_APP_NEST_API_URL');
    if (!base) return null;
    return `${base.replace(/\/$/, '')}/auth/profile`;
}

function isAuthProfileRequest(url: string): boolean {
    return url.includes('/auth/profile');
}

let sessionCheck: Promise<boolean> | null = null;

/** True when the cookie session is still valid, or the check itself failed temporarily. */
function sessionStillValid(): Promise<boolean> {
    if (!sessionCheck) {
        const url = profileUrl();
        sessionCheck = (
            url
                ? axios.get(url, { withCredentials: true }).then(() => true)
                : Promise.resolve(false)
        )
            .catch((profileError) => {
                const status = profileError?.response?.status;
                // A confirmed 401 is a dead session. Timeouts and 5xx are not.
                return status !== 401;
            })
            .finally(() => {
                sessionCheck = null;
            });
    }
    return sessionCheck;
}

function redirectToLogin(): void {
    localStorage.removeItem('haat_auth_session');
    localStorage.removeItem('haat_business_info_v2');
    window.location.href = '/login';
}

function rethrow(error: any, status: number | null): never {
    const body = error?.response?.data;
    if (body && typeof body === 'object') {
        throw Object.assign(body, { status: body.status ?? status });
    }
    throw error;
}

// Handle errors in a centralized function
async function handleError(error: any): Promise<never> {
    const status = error.response ? error.response.status : null;
    const message = error.response ? error.response.data : "An unknown error occurred";
    const url = error.request
        ? error.request.responseURL
        : "Unknown URL";
    switch (status) {
        case 400:
            console.error("Bad Request:", message);
            break;
        case 401:
            if (url !== CRM_LOGIN_URL) {
                const confirmedLoggedOut =
                    isAuthProfileRequest(url) || !(await sessionStillValid());
                if (confirmedLoggedOut) {
                    redirectToLogin();
                }
            }
            break;
        case 403:
            console.error("Forbidden:", message);
            break;
        case 404:
            console.error("Not Found:", message);
            break;
        case 500:
            console.error("Internal Server Error:", message);
            break;
        default:
            console.error("API request error:", message);
            break;
    }

    rethrow(error, status);
}

// Include token in headers for each request
function getRequestHeaders(additionalHeaders = {}): AxiosRequestConfig {
    return {
        withCredentials: true,
        headers: {
            ...additionalHeaders
        }
    };
}

function getRequestHeadersResturant(additionalHeaders = {}): AxiosRequestConfig {
    // console.log("getRequestHeadersResturant")
    const token = getTokenResturant();
    return {
        headers: {
            'Authorization': `Bearer ${token}`,
            ...additionalHeaders
        }
    };
}

// GET request function
export async function getRequest<T>(url: string, headers = {}): Promise<ApiServiceResponse<T>> {
    try {
        const config = url.includes("voice-center") ? getRequestHeadersResturant(headers) : getRequestHeaders(headers);
        const response: AxiosResponse<T> = await axios.get(url, config);
        return { data: response.data, status: response.status };
    } catch (error) {
        return handleError(error);
    }
}

// POST request function
export async function postRequest<T>(url: string, data: any, headers = {}): Promise<ApiServiceResponse<T>> {
    try {
        // console.log("postRequest")
        const config = url.includes("haat-insert-prod") ? getRequestHeadersResturant(headers) : getRequestHeaders(headers);

        // const config = getRequestHeaders(headers);
        const response: AxiosResponse<T> = await axios.post(url, data, config);
        return { data: response.data, status: response.status };
    } catch (error) {
        return handleError(error);
    }
}

export async function patchRequest<T>(url: string, data: any, headers = {}): Promise<ApiServiceResponse<T>> {
    try {
        const config = getRequestHeaders(headers);
        const response: AxiosResponse<T> = await axios.patch(url, data, config);
        return { data: response.data, status: response.status };
    } catch (error) {
        return handleError(error);
    }
}

export async function deleteRequest<T>(url: string, headers = {}): Promise<ApiServiceResponse<T>> {
    try {
        const config = getRequestHeaders(headers);
        const response: AxiosResponse<T> = await axios.delete(url, config);
        return { data: response.data, status: response.status };
    } catch (error) {
        return handleError(error);
    }
}
